// ---------------------------------------------------------------------------
// The single status chip a lead row shows — and its precedence
//
// The Status column shows ONE chip: the furthest / most operative state the
// lead has reached (the rationale for one chip rather than three lives on
// `leadStateChip` in campaign-detail.ts). This module holds the ORDER and the
// pure predicates, extracted so the precedence can be tested directly rather
// than only by rendering the page.
//
// PRECEDENCE, 20 August: opt-out now outranks replied. A lead can legitimately
// carry both facts — they live in different places (`status`/`repliedAt` for
// engagement, the global Do Not Contact list for suppression) and both can be
// true at once. When they are, the operative one wins the chip: "will not be
// contacted" dominates "replied". The list route already sets
// `status:'opted_out'` + `suppressed:true` for a suppressed lead
// (routes/campaign-leads.ts), but `repliedAt` stays set, and the chip used to
// rank `replied` first — so a lead who replied AND opted out showed "replied",
// contradicting the Do Not Contact banner above the same table. The reply is
// not lost: it stays in the lead's Engagement/Messages detail and in the
// Replied filter (which still matches on `repliedAt`).
// ---------------------------------------------------------------------------

/** The lead fields the pure chip predicates read. A subset of the page's LeadData. */
export interface LeadStateChipInput {
  status?: string;
  /**
   * Operator's Manual Overwrite. When set it WINS the chip — the whole point of
   * a manual override — except a global Do-Not-Contact suppression, which is a
   * hard safety fact and still shows. See resolveLeadStateChip.
   */
  manualStatus?: string;
  repliedAt?: string;
  bouncedAt?: string;
  /** On the global Do Not Contact list (routes/campaign-leads.ts sets this). */
  suppressed?: boolean;
  /**
   * On the global pre-existing-connection list — already a connection, or an
   * outstanding invitation, before any campaign. Shows the "skipped — already
   * connected" chip even for a pure email lead that has no LinkedIn observation
   * of its own to derive it from. Unlike `suppressed` it does NOT rewrite status.
   */
  knownConnection?: boolean;
  dmSentAt?: string;
  acceptedAt?: string;
  alreadyConnected?: string;
  invitePendingElsewhere?: string;
  connectionSentAt?: string;
  connectionState?: string;
  doneAt?: string;
}

export type LeadStateChipKey =
  | 'optedOut'
  | 'replied'
  | 'bounced'
  | 'done'
  | 'dmSent'
  | 'accepted'
  | 'skippedAlreadyConnected'
  | 'alreadyConnected'
  | 'inviteSentIgnored'
  | 'inviteReceived'
  | 'connSent'
  | 'invitePending'
  | 'noInvite'
  | 'connectionUnknown'
  | 'contacted'
  | 'pending';

export interface LeadStateChip {
  key: LeadStateChipKey;
  label: string;
  cls: string;
  icon: string;
}

/**
 * Pure predicates for the chip keys that depend only on the lead's own fields.
 * `done` and `skippedAlreadyConnected` are NOT here: they need context the row
 * knows and the lead does not (whether a draft was sent; whether the campaign
 * has a DM step), so `resolveLeadStateChip` takes them as booleans instead.
 *
 * These are the SAME predicates the filter dropdown uses — campaign-detail.ts
 * imports this map for those buckets, so a chip and its bucket can never
 * disagree.
 */
export const chipPredicates: Record<
  Exclude<LeadStateChipKey, 'done' | 'skippedAlreadyConnected'>,
  (l: LeadStateChipInput) => boolean
> = {
  // A lead suppressed globally is opted out even if THIS row's status was left
  // 'replied' by an answer here — the opt-out was processed on another row.
  optedOut: l => l.status === 'opted_out' || !!l.suppressed,
  replied: l => !!l.repliedAt || l.status === 'replied',
  bounced: l => !!l.bouncedAt || l.status === 'bounced',
  dmSent: l => !!l.dmSentAt,
  accepted: l => !!l.acceptedAt,
  alreadyConnected: l => !!l.alreadyConnected,
  inviteSentIgnored: l => l.connectionState === 'invite_sent_ignored',
  inviteReceived: l => l.connectionState === 'invite_received_pending',
  connSent: l => !!l.connectionSentAt && !l.alreadyConnected && !l.invitePendingElsewhere,
  invitePending: l => !!l.invitePendingElsewhere && !l.acceptedAt && !l.alreadyConnected,
  noInvite: l => l.connectionState === 'not_connected_no_invite',
  connectionUnknown: l => l.connectionState === 'unknown',
  contacted: l => l.status === 'contacted',
  pending: l => l.status === 'pending',
};

/**
 * The chips in priority order — first match wins.
 *
 * Terminal outcomes first (they override everything behind them), with opt-out
 * ahead of replied and bounced: it is the strongest "do not contact this
 * person again" signal and must not be masked by an earlier reply. Then
 * progress through the sequence, furthest first.
 */
export const LEAD_STATE_CHIPS: LeadStateChip[] = [
  { key: 'optedOut', label: 'opted out', cls: 'badge-warning', icon: 'block' },
  { key: 'replied', label: 'replied', cls: 'badge-success', icon: 'reply' },
  { key: 'bounced', label: 'bounced', cls: 'badge-error', icon: 'error' },
  { key: 'done', label: 'done', cls: 'badge-success badge-outline', icon: 'task-alt' },
  { key: 'dmSent', label: 'DM sent', cls: 'badge-info', icon: 'send' },
  { key: 'accepted', label: 'accepted', cls: 'badge-success', icon: 'how-to-reg' },
  {
    key: 'skippedAlreadyConnected',
    label: 'skipped — already connected',
    cls: 'badge-warning badge-outline',
    icon: 'link',
  },
  { key: 'alreadyConnected', label: 'already connected', cls: 'badge-outline', icon: 'link' },
  { key: 'inviteSentIgnored', label: 'skipped — invite ignored', cls: 'badge-warning badge-outline', icon: 'person-off' },
  { key: 'inviteReceived', label: 'they invited us', cls: 'badge-accent', icon: 'call-received' },
  { key: 'connSent', label: 'invite sent', cls: 'badge-info', icon: 'person-add' },
  { key: 'invitePending', label: 'invite pending', cls: 'badge-warning', icon: 'hourglass-top' },
  { key: 'noInvite', label: 'no invite', cls: 'badge-ghost', icon: 'link-off' },
  { key: 'connectionUnknown', label: 'state unknown', cls: 'badge-error badge-outline', icon: 'help-outline' },
  { key: 'contacted', label: 'contacted', cls: 'badge-info', icon: 'outgoing-mail' },
  { key: 'pending', label: 'pending', cls: 'badge-ghost', icon: 'schedule' },
];

/** A manual override value → the chip key it displays as. */
const MANUAL_STATUS_CHIP_KEY: Record<string, LeadStateChipKey> = {
  pending: 'pending',
  contacted: 'contacted',
  replied: 'replied',
  bounced: 'bounced',
  opted_out: 'optedOut',
};

const CHIP_BY_KEY = new Map<LeadStateChipKey, LeadStateChip>(LEAD_STATE_CHIPS.map(c => [c.key, c]));

/**
 * The chip a lead row should show, or `null` for a status this list does not
 * model (the caller renders that as itself rather than as nothing).
 *
 * A global Do-Not-Contact suppression outranks everything, including a manual
 * override — it is a hard safety fact and must never be masked (the same reason
 * opt-out outranks replied). A TERMINAL manual override (opted out/replied/
 * bounced) then wins over every computed state. A SOFT manual label (pending/
 * contacted) wins over the computed states too — surviving a leftover timestamp
 * — EXCEPT "skipped — already connected", which shows over it: the row's warning
 * already says nothing was auto-sent, and the Status chip must agree.
 *
 * `isCompleted` and `isSkippedAlreadyConnected` are computed by the row, which
 * knows the campaign's steps and the drafts; everything else is read from the
 * lead directly through `chipPredicates`.
 */
export function resolveLeadStateChip(
  lead: LeadStateChipInput,
  opts: { isCompleted: boolean; isSkippedAlreadyConnected: boolean },
): LeadStateChip | null {
  // Global Do Not Contact is a hard safety fact — it shows even over a manual label.
  if (lead.suppressed) return CHIP_BY_KEY.get('optedOut') ?? null;

  const manualKey = lead.manualStatus ? MANUAL_STATUS_CHIP_KEY[lead.manualStatus] : undefined;
  const manualChip = manualKey ? CHIP_BY_KEY.get(manualKey) : undefined;

  // A TERMINAL manual override (opted out / replied / bounced) is the operator
  // saying "stop this lead", and still wins over every computed state.
  const manualIsTerminal = manualKey === 'optedOut' || manualKey === 'replied' || manualKey === 'bounced';
  if (manualChip && manualIsTerminal) return manualChip;

  // But a SOFT manual label (pending / contacted) does not mask "already
  // connected, nothing was auto-sent": the warning on the row already says it,
  // and the Status chip must agree rather than read a plain "pending". Only over
  // the manual label — the loop below keeps replied/bounced/done AHEAD of it for
  // a lead with no override, so this cannot hide a reply on an unmarked lead.
  const skippedAlreadyConnected = opts.isSkippedAlreadyConnected || !!lead.knownConnection;
  if (manualChip && skippedAlreadyConnected) return CHIP_BY_KEY.get('skippedAlreadyConnected') ?? null;

  // Otherwise the soft manual label wins over the rest of the computed states,
  // so the operator's override survives a leftover timestamp.
  if (manualChip) return manualChip;

  for (const chip of LEAD_STATE_CHIPS) {
    const matches =
      chip.key === 'done'
        ? opts.isCompleted || !!lead.doneAt
        : chip.key === 'skippedAlreadyConnected'
          ? skippedAlreadyConnected
          : chipPredicates[chip.key](lead);
    if (matches) return chip;
  }
  return null;
}
